import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Card, CardContent } from '../../../components/ui/Card';

const BulkActions = ({ selectedCount, onExport, onBulkStatusUpdate, onImportVendors, onBulkDelete }) => {
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  const exportOptions = [
    { value: 'csv', label: 'Export as CSV' },
    { value: 'excel', label: 'Export as Excel' },
    { value: 'pdf', label: 'Export as PDF' }
  ];

  const statusOptions = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
    { value: 'PENDING', label: 'Pending Review' },
    { value: 'SUSPENDED', label: 'Suspended' }
  ];

  const handleBulkExport = (format) => {
    onExport(format);
    setShowBulkMenu(false);
  };

  const handleStatusUpdate = () => {
    if (selectedStatus) {
      onBulkStatusUpdate(selectedStatus);
      setSelectedStatus('');
      setShowStatusUpdate(false);
    }
  };

  const handleImportClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        onImportVendors(file);
      }
    };
    input.click();
  };

  return (
    <Card className="border border-cos-line shadow-sm bg-white">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
          {/* Selection Info */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cos-surface-muted rounded-lg flex items-center justify-center">
              <Icon name="CheckSquare" size={20} className="text-cos-ink-secondary" />
            </div>
            <span className="text-sm font-semibold text-cos-ink">
              {selectedCount > 0 ? (
                `${selectedCount} vendor${selectedCount > 1 ? 's' : ''} selected`
              ) : (
                'No vendors selected'
              )}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Import Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleImportClick}
              iconName="Upload"
              iconPosition="left"
            >
              Import Vendors
            </Button>

            {selectedCount > 0 && (
              <>
                {/* Bulk Export Dropdown */}
                <div className="relative">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setShowBulkMenu(!showBulkMenu)}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Export Selected
                    <Icon name="ChevronDown" size={14} className="ml-1" />
                  </Button>

                  {showBulkMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowBulkMenu(false)}
                      />
                      <div className="absolute right-0 mt-2 w-48 bg-white border border-cos-line rounded-lg shadow-lg z-20 overflow-hidden">
                        <div className="p-2">
                          {exportOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => handleBulkExport(option.value)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-cos-ink-secondary hover:text-cos-ink hover:bg-cos-surface-hover rounded-md transition-colors font-medium"
                            >
                              <Icon name="FileText" size={16} />
                              <span>{option.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Bulk Status Update */}
                <div className="relative">
                  {showStatusUpdate && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowStatusUpdate(false)}
                      />
                      <div className="absolute right-0 mt-2 w-64 bg-white border border-cos-line rounded-lg shadow-lg z-20 p-4">
                        <div className="space-y-3">
                          <h4 className="font-medium text-cos-ink">Update Status</h4>
                          <Select
                            placeholder="Select new status"
                            options={statusOptions}
                            value={selectedStatus}
                            onChange={setSelectedStatus}
                          />
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="default"
                              size="sm"
                              onClick={handleStatusUpdate}
                              disabled={!selectedStatus}
                              className="flex-1"
                            >
                              Update
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setShowStatusUpdate(false);
                                setSelectedStatus('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Delete Selected */}
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={onBulkDelete}
                  iconName="Trash2"
                  iconPosition="left"
                >
                  Delete Selected
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        {selectedCount === 0 && (
          <div className="mt-4 pt-4 border-t border-cos-line">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-cos-ink">1,247</div>
                <div className="text-sm text-cos-ink-secondary">Total Vendors</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600">892</div>
                <div className="text-sm text-cos-ink-secondary">Active</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">156</div>
                <div className="text-sm text-cos-ink-secondary">Pending</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">23</div>
                <div className="text-sm text-cos-ink-secondary">Rejected</div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BulkActions;